import getMatrixType from "@secureo/common/utils/getMatrixType"
import { DispatchLockingSystemReducer, LockingSystemReducerState } from ".."

type MoveComponentPayload = Pick<
	LockingSystemReducerState,
	"matrix" | "matrixType" | "keyComponentsUuids" | "lockingComponentsUuids"
>

export type MoveDirection = "up" | "down"

const getSwapIndex = (
	targetIndex: number,
	direction: MoveDirection,
	numberOfComponents: number,
) => {
	const lastIndex = numberOfComponents - 1

	const isFirstElement = targetIndex === 0
	const isLastElement = targetIndex === lastIndex

	if (isFirstElement && direction === "down") return lastIndex
	if (isLastElement && direction === "up") return 0

	const swapIndex = direction === "up" ? targetIndex + 1 : targetIndex - 1
	return swapIndex
}

const getReducerPayloadAfterMovingLockingComponent = (
	targetUuid: string,
	reducerState: LockingSystemReducerState,
	direction: MoveDirection,
) => {
	const { lockingComponentsUuids, matrix } = reducerState

	const targetIndex = lockingComponentsUuids.findIndex((rowUuid) => rowUuid === targetUuid)
	const swapIndex = getSwapIndex(targetIndex, direction, lockingComponentsUuids.length)

	lockingComponentsUuids[targetIndex] = lockingComponentsUuids[swapIndex]
	lockingComponentsUuids[swapIndex] = targetUuid

	const targetRow = [...matrix[targetIndex]]
	const rowToSwap = [...matrix[swapIndex]]

	// Swap rows
	matrix[targetIndex] = rowToSwap
	matrix[swapIndex] = targetRow

	return { matrix, lockingComponentsUuids, keyComponentsUuids: reducerState.keyComponentsUuids }
}

const getReducerPayloadAfterMovingKeyComponent = (
	targetUuid: string,
	reducerState: LockingSystemReducerState,
	direction: MoveDirection,
) => {
	const { keyComponentsUuids, matrix } = reducerState

	const targetIndex = keyComponentsUuids.findIndex((rowUuid) => rowUuid === targetUuid)
	const swapIndex = getSwapIndex(targetIndex, direction, keyComponentsUuids.length)

	keyComponentsUuids[targetIndex] = keyComponentsUuids[swapIndex]
	keyComponentsUuids[swapIndex] = targetUuid

	// Swap target/swap values in each row
	for (let rowIndex = 0; rowIndex < matrix.length; rowIndex++) {
		const row = matrix[rowIndex]

		// Swap values
		const tempValue = row[swapIndex]
		row[swapIndex] = row[targetIndex]
		row[targetIndex] = tempValue
	}

	return {
		matrix,
		lockingComponentsUuids: reducerState.lockingComponentsUuids,
		keyComponentsUuids,
	}
}

export const moveComponent = (
	dispatch: DispatchLockingSystemReducer,
	reducerState: LockingSystemReducerState,
	uuid: string,
	direction: MoveDirection,
) => {
	const { type } = reducerState?.componentsByUuid?.[uuid] ?? {}

	const {
		matrix: updatedMatrix,
		lockingComponentsUuids,
		keyComponentsUuids,
	} = type === "lock"
		? getReducerPayloadAfterMovingLockingComponent(uuid, reducerState, direction)
		: getReducerPayloadAfterMovingKeyComponent(uuid, reducerState, direction)

	const matrixType = getMatrixType(updatedMatrix)

	const payload: MoveComponentPayload = {
		matrix: updatedMatrix,
		matrixType,
		lockingComponentsUuids,
		keyComponentsUuids,
	}

	dispatch({
		type: "MOVE_COMPONENT",
		payload,
	})
}
